import { useState } from 'react'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import 'react-photo-view/dist/react-photo-view.css'
import { Loader2 } from '@/components/icons'
import { isNative, openWithNativeViewer } from '@/lib/native-file-viewer'

export interface GridImage {
  id: string
  // Original — used for the lightbox / native viewer and the large tiles.
  url: string | null
  // Optional 400px square variant for the small square tiles.
  thumbUrl?: string | null
  name?: string
}

interface ImageGridProps {
  images: GridImage[]
  loading?: boolean
}

export function ImageGrid({ images, loading }: ImageGridProps) {
  if (images.length === 0) return null

  const hasAnyUrl = images.some((i) => i.url)
  if (loading && !hasAnyUrl) {
    return (
      <div className="bg-background-brand-bold flex items-center gap-2 px-2 h-8 rounded-[10px] w-full">
        <Loader2 className="w-4 h-4 text-white shrink-0 animate-spin" />
        <p className="text-white text-[12px] leading-normal opacity-70">Loading images...</p>
      </div>
    )
  }

  const grid = <GridLayout images={images} />
  return isNative ? grid : <PhotoProvider>{grid}</PhotoProvider>
}

function GridLayout({ images }: { images: GridImage[] }) {
  const count = images.length

  if (count === 1) {
    const img = images[0]
    if (!img.url) return null
    return (
      <ImageTap url={img.url} fileName={img.name}>
        <img
          src={img.url}
          alt={img.name ?? ''}
          className="max-w-full max-h-[400px] rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
        />
      </ImageTap>
    )
  }

  if (count === 2) {
    return (
      <div className="grid grid-cols-2 gap-1 rounded-lg overflow-hidden">
        {images.map((img) => {
          if (!img.url)
            return <div key={img.id} className="aspect-square bg-surface-L2 animate-pulse" />
          return (
            <ImageTap key={img.id} url={img.url} fileName={img.name}>
              <img
                src={img.thumbUrl ?? img.url}
                alt={img.name ?? ''}
                loading="lazy"
                decoding="async"
                className="w-full aspect-square object-cover cursor-pointer hover:opacity-80 transition-opacity"
              />
            </ImageTap>
          )
        })}
      </div>
    )
  }

  if (count === 3) {
    const [first, second, third] = images
    return (
      <div className="flex flex-col gap-1 rounded-lg overflow-hidden">
        {first.url ? (
          <ImageTap url={first.url} fileName={first.name}>
            <img
              src={first.url}
              alt={first.name ?? ''}
              className="w-full aspect-video object-cover cursor-pointer hover:opacity-80 transition-opacity"
            />
          </ImageTap>
        ) : (
          <div className="w-full aspect-video bg-surface-L2 animate-pulse" />
        )}
        <div className="grid grid-cols-2 gap-1">
          {[second, third].map((img) => {
            if (!img.url)
              return <div key={img.id} className="aspect-square bg-surface-L2 animate-pulse" />
            return (
              <ImageTap key={img.id} url={img.url} fileName={img.name}>
                <img
                  src={img.thumbUrl ?? img.url}
                  alt={img.name ?? ''}
                  loading="lazy"
                  decoding="async"
                  className="w-full aspect-square object-cover cursor-pointer hover:opacity-80 transition-opacity"
                />
              </ImageTap>
            )
          })}
        </div>
      </div>
    )
  }

  // Four or more: every photo gets a tile (no "+N" cap — on native there is no
  // lightbox to reach hidden photos through). Three columns to match the
  // edit-mode grids.
  return (
    <div className="grid grid-cols-3 gap-1 rounded-lg overflow-hidden">
      {images.map((img) => {
        if (!img.url)
          return <div key={img.id} className="aspect-square bg-surface-L2 animate-pulse" />
        return (
          <ImageTap key={img.id} url={img.url} fileName={img.name}>
            <img
              src={img.thumbUrl ?? img.url}
              alt={img.name ?? ''}
              loading="lazy"
              decoding="async"
              className="w-full aspect-square object-cover cursor-pointer hover:opacity-80 transition-opacity"
            />
          </ImageTap>
        )
      })}
    </div>
  )
}

function ImageTap({
  url,
  fileName,
  children,
}: {
  url: string
  fileName?: string
  children: React.ReactElement
}) {
  const [opening, setOpening] = useState(false)

  if (isNative) {
    const handleClick = async () => {
      if (opening) return
      setOpening(true)
      try {
        await openWithNativeViewer(url, fileName)
      } finally {
        setOpening(false)
      }
    }
    return (
      <div onClick={handleClick} className="relative cursor-pointer">
        {children}
        {opening && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-lg">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        )}
      </div>
    )
  }
  return <PhotoView src={url}>{children}</PhotoView>
}
